import { ActionTree } from 'vuex'
import { Auth } from '@/hexapla-core/domains/accounts'
import { FlashMessage, HexaplaState, Reader } from '@/store/state'
import { Lemma } from '@/hexapla-core/domains/lemmas'
import { Contributor } from '@/hexapla-core/domains/accounts'
import { Book, Translator, TranslatorRelationship, Witness, WitnessType } from '@/hexapla-core/domains/resources'
import { ensureReferenceData } from '@/store/actions/referenceData'
import { baseTexts } from '@/hexapla-core/domains/lemmas/edition'
import { authorship } from '@/hexapla-core/domains/lemmas/wizardDraft'
import { LINE_CONTEXT_KEYS } from '@/hexapla-core/domains/lemmas/span'

export enum ActionTypes {
  UNLOCK = 'unlock',
  LOCK = 'lock',
  END_SESSION = 'endSession',
  NOTIFY = 'notify',
  REGISTER_VIEW_MESSAGE = 'registerViewMessage',
  CLOSE_VIEW_MESSAGE = 'closeViewMessage',
  REMOVE_FLASH = 'removeFlash',
  TOGGLE_LOADING = 'toggleLoading',
  WITH_LOADING = 'withLoading',
  SET_LEMMAS = 'setLemmas',
  SET_CONTRIBUTOR = 'setContributor',
  SET_BOOKS = 'setBooks',
  SET_TRANSLATORS = 'setTranslators',
  SET_TRANSLATOR_RELATIONSHIPS = 'setTranslatorRelationships',
  SET_WITNESSES = 'setWitnesses',
  SET_WITNESS_TYPES = 'setWitnessTypes',
  ENSURE_REFERENCE_DATA = 'ensureReferenceData',
  UPDATE_READER = 'updateReader',
  UPDATE_NEW_LEMMA = 'updateNewLemma',
  RESET_NEW_LEMMA = 'resetNewLemma',
  EDIT_LEMMA = 'editLemma',
  TOGGLE_SETTING = 'toggleSetting',
}

type HexaplaActions = ActionTree<HexaplaState, HexaplaState>
const actions: HexaplaActions = {
  [ActionTypes.UNLOCK]: ({ commit }, auth: Auth): void => commit('UNLOCK', auth),
  // Deliberate sign-out: clears the session and the lemma in progress.
  [ActionTypes.LOCK]: ({ commit }): void => commit('LOCK'),
  // The server rejected the session: clears it but keeps the lemma in progress.
  [ActionTypes.END_SESSION]: ({ commit }): void => commit('SESSION_ENDED'),
  [ActionTypes.NOTIFY]: ({ commit }, flash: FlashMessage): void =>
    commit('PUSH_FLASH', {
      message: flash.message,
      level: flash.level,
    }),
  [ActionTypes.REGISTER_VIEW_MESSAGE]: ({ commit }, view: string): void => commit('REGISTER_VIEW_MESSAGE', view),
  [ActionTypes.CLOSE_VIEW_MESSAGE]: ({ commit }, view: string): void => commit('CLOSE_VIEW_MESSAGE', view),
  [ActionTypes.REMOVE_FLASH]: ({ commit }, index: number): void => commit('REMOVE_FLASH', index),
  [ActionTypes.TOGGLE_LOADING]: ({ commit }): void => commit('TOGGLE_APP_IS_LOADING'),
  [ActionTypes.WITH_LOADING]: async ({ commit }, callback: () => void): Promise<void> => {
    commit('TOGGLE_APP_IS_LOADING')
    await callback()
    commit('TOGGLE_APP_IS_LOADING')
  },
  [ActionTypes.SET_LEMMAS]: ({ commit }, lemmas: Lemma[]): void => commit('SET_LEMMAS', lemmas),
  [ActionTypes.SET_CONTRIBUTOR]: ({ commit }, contributor: Contributor): void => commit('SET_CONTRIBUTOR', contributor),
  [ActionTypes.SET_BOOKS]: ({ commit }, books: Book[]): void => commit('SET_BOOKS', books),
  [ActionTypes.SET_TRANSLATORS]: ({ commit }, translators: Translator[]): void =>
    commit('SET_TRANSLATORS', translators),
  [ActionTypes.SET_TRANSLATOR_RELATIONSHIPS]: ({ commit }, rels: TranslatorRelationship[]): void =>
    commit('SET_TRANSLATOR_RELATIONSHIPS', rels),
  [ActionTypes.SET_WITNESSES]: ({ commit }, witnesses: Witness[]): void => commit('SET_WITNESSES', witnesses),
  [ActionTypes.SET_WITNESS_TYPES]: ({ commit }, types: WitnessType[]): void => commit('SET_WITNESS_TYPES', types),
  // Load-if-missing for shared reference data; see `referenceData.ts`.
  [ActionTypes.ENSURE_REFERENCE_DATA]: ensureReferenceData,
  [ActionTypes.UPDATE_READER]: ({ commit }, updatedFields: Partial<Reader>): void =>
    commit('UPDATE_READER', updatedFields),
  // Every change to the wizard's lemma carries its authorship (wizardDraft).
  [ActionTypes.UPDATE_NEW_LEMMA]: ({ commit, state }, updatedFields: Partial<Lemma>): void => {
    const contributor = state.user.contributor
    const author = authorship({ ...state.newLemma, ...updatedFields }, contributor ? Number(contributor.id) : null)
    commit('UPDATE_NEW_LEMMA', { ...updatedFields, ...author })
  },
  // The lemma in the wizard is finished with: start the next one clean.
  [ActionTypes.RESET_NEW_LEMMA]: ({ commit }): void => commit('RESET_NEW_LEMMA'),
  // Open an existing lemma in the wizard. The wizard becomes exactly this lemma
  // (nothing kept from a draft that was open) and records that holding it is
  // intended, so entering the wizard does not treat it as left behind.
  [ActionTypes.EDIT_LEMMA]: ({ commit, state }, lemma: Lemma): void => {
    commit('RESET_NEW_LEMMA')
    commit('UPDATE_NEW_LEMMA', {
      id: lemma.id,
      lemma: lemma.lemma,
      lemma_start: lemma.lemma_start,
      lemma_stop: lemma.lemma_stop,
      ...Object.fromEntries(LINE_CONTEXT_KEYS.map((key) => [key, lemma[key] ?? null])),
      hebrew: lemma.hebrew,
      base_texts: baseTexts(lemma.base_texts),
      sub_verse: lemma.sub_verse ?? null,
      texts: lemma.texts,
      attestations: lemma.attestations,
      // Every field the lemma has, or the save that follows would send it
      // empty: a list the wizard never loaded is not a list the editor emptied.
      notes: lemma.notes ?? [],
      create_contributor_id: lemma.create_contributor_id,
      update_contributor_id: (state.user.contributor?.id ?? null) as Lemma['update_contributor_id'],
    })
    commit('SET_EDITING_LEMMA', lemma.id)
  },
  [ActionTypes.TOGGLE_SETTING]: ({ commit }, setting: string): void => commit('settings/toggle', setting),
}

export default actions
